import axios, { type AxiosResponse, type AxiosRequestConfig, type AxiosError } from "axios";

import { NON_SESSION_401_CODES, SECURITY_PIN_RESET_REQUIRED_CODE } from "@/shared/utils/twoFactorCodes";

import { ConfigService } from "./config.service";
import { TokenStorageService } from "./tokenStorage.service";

export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  status: number;
}

export interface ApiError {
  /**
   * The global `ValidationPipe` answers one sentence per broken rule, as an ARRAY; every hand-thrown
   * exception answers a single string. Typing this `string` is what let `getErrorMessage` hand the array
   * straight to React, which renders the sentences run together with no separator.
   */
  message: string | string[];
  /**
   * Present only when the exception was thrown as an object — which, across the whole payments module, is
   * exactly one call site. Never assume it is there.
   */
  code?: string;
  errors?: Record<string, string[]>;
}

export type HttpRequestConfig<D = unknown> = Omit<AxiosRequestConfig<D>, "baseURL" | "headers"> & {
  headers?: Record<string, string>;
};

const configService = ConfigService.getInstance();

export class HttpApiService {
  private static axiosInstance = axios.create({
    baseURL: configService.getOrThrow("colocaApiUrl"),
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Single-flight: concurrent 401s share one refresh call. Resolves to the new
  // access token, or null when the session is gone and re-login is required.
  private static refreshPromise: Promise<string | null> | null = null;

  private static refreshAccessToken(): Promise<string | null> {
    if (!HttpApiService.refreshPromise) {
      // The refresh token is sent in the body for the local bearer flow; off
      // localhost it is null and the httpOnly cookie is authoritative.
      const refreshToken = TokenStorageService.getRefreshToken() ?? undefined;
      HttpApiService.refreshPromise = HttpApiService.axiosInstance
        .post<{ access_token?: string; refresh_token?: string }>("/auth/refresh", {
          refresh_token: refreshToken,
        })
        .then((response) => {
          const newToken = response.data?.access_token ?? null;
          // No-op off localhost (cookie is authoritative in production).
          if (newToken) TokenStorageService.setToken(newToken);
          if (response.data?.refresh_token) {
            TokenStorageService.setRefreshToken(response.data.refresh_token);
          }
          return newToken;
        })
        .catch(() => {
          TokenStorageService.clearToken();
          TokenStorageService.clearRefreshToken();
          return null;
        })
        .finally(() => {
          HttpApiService.refreshPromise = null;
        });
    }
    return HttpApiService.refreshPromise;
  }

  static {
    HttpApiService.axiosInstance.interceptors.request.use(
      (config) => {
        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        }
        const language = localStorage.getItem("language");
        if (language) {
          config.headers["Accept-Language"] = language;
        }

        // Add Authorization header for localhost. Never clobber an Authorization
        // header already set on the request config — the public verification flow
        // (marco B) injects its own identity token per request.
        const token = TokenStorageService.getToken();
        if (token && !config.headers["Authorization"]) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    HttpApiService.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
        const status = error.response?.status;
        const url = original?.url ?? "";

        // On 401, silently refresh once and replay. Skip the auth endpoints to
        // avoid recursion; `_retry` caps it at one attempt per request.
        const isAuthFlow = url.includes("/auth/refresh") || url.includes("/auth/login");

        // Public requests authenticate with their own per-request token,
        // outside the dashboard session lifecycle. A 401 here must not trigger
        // `/auth/refresh` — on failure that would clear the staff's dashboard
        // session token. Let it propagate so the public flow shows its own
        // expired-link screen.
        const isPublicRequest = url.includes("/public/");

        // A refused step-up code or password is a 401 on a valid session: refreshing changes nothing, and the
        // replay would spend a second attempt against the security PIN's lock.
        const refusedInput =
          status === 401 &&
          NON_SESSION_401_CODES.includes((error.response?.data as { code?: string } | undefined)?.code ?? "");

        if (
          status === 401 &&
          original &&
          !original._retry &&
          !isAuthFlow &&
          !isPublicRequest &&
          !refusedInput
        ) {
          original._retry = true;
          const newToken = await HttpApiService.refreshAccessToken();
          if (newToken !== null) {
            return HttpApiService.axiosInstance(original);
          }
          return Promise.reject(error);
        }

        if (status === 403) {
          const data = error.response?.data as { code?: string } | undefined;
          if (data?.code === "TWO_FACTOR_REQUIRED") {
            window.dispatchEvent(new CustomEvent("auth:two-factor-required"));
          }
        }
        // The PIN's tenth wrong attempt also closes every session of the user server-side. The challenge keeps
        // its own message; `useStepUpLockoutWatcher` checks whether the session survived and signs out cleanly.
        if (
          status === 401 &&
          (error.response?.data as { code?: string } | undefined)?.code === SECURITY_PIN_RESET_REQUIRED_CODE
        ) {
          window.dispatchEvent(new CustomEvent("auth:step-up-reset-required"));
        }
        return Promise.reject(error);
      },
    );
  }

  public static async get<TResponse>(
    url: string,
    config?: HttpRequestConfig,
  ): Promise<AxiosResponse<TResponse>> {
    return HttpApiService.axiosInstance.get<TResponse>(url, config);
  }

  public static async post<TResponse, TRequest = unknown>(
    url: string,
    data: TRequest,
    config?: HttpRequestConfig<TRequest>,
  ): Promise<AxiosResponse<TResponse>> {
    return HttpApiService.axiosInstance.post<TResponse, AxiosResponse<TResponse>, TRequest>(
      url,
      data,
      config,
    );
  }

  public static async put<TResponse, TRequest = unknown>(
    url: string,
    data: TRequest,
    config?: HttpRequestConfig<TRequest>,
  ): Promise<AxiosResponse<TResponse>> {
    return HttpApiService.axiosInstance.put<TResponse, AxiosResponse<TResponse>, TRequest>(url, data, config);
  }

  public static async patch<TResponse, TRequest = unknown>(
    url: string,
    data: Partial<TRequest>,
    config?: HttpRequestConfig<Partial<TRequest>>,
  ): Promise<AxiosResponse<TResponse>> {
    return HttpApiService.axiosInstance.patch<TResponse, AxiosResponse<TResponse>, Partial<TRequest>>(
      url,
      data,
      config,
    );
  }

  public static async delete<TResponse = void>(
    url: string,
    config?: HttpRequestConfig,
  ): Promise<AxiosResponse<TResponse>> {
    return HttpApiService.axiosInstance.delete<TResponse>(url, config);
  }

  public static isAxiosError(error: unknown): error is AxiosError<ApiError> {
    return axios.isAxiosError(error);
  }

  public static isUnauthorizedError(error: unknown): boolean {
    return HttpApiService.isAxiosError(error) && error.response?.status === 401;
  }

  public static isForbiddenError(error: unknown): boolean {
    return HttpApiService.isAxiosError(error) && error.response?.status === 403;
  }

  public static isNotFoundError(error: unknown): boolean {
    return HttpApiService.isAxiosError(error) && error.response?.status === 404;
  }

  public static isConflictError(error: unknown): boolean {
    return HttpApiService.isAxiosError(error) && error.response?.status === 409;
  }

  public static isAccessDeniedError(error: unknown): boolean {
    return HttpApiService.isUnauthorizedError(error) || HttpApiService.isForbiddenError(error);
  }

  public static getErrorMessage(error: unknown): string {
    if (HttpApiService.isAxiosError(error)) {
      const message = error.response?.data?.message;
      // Joining rather than returning the array: every caller declares a `string` and renders it directly.
      // Nothing else about the behaviour changes — a single-string message takes the same path it always did.
      if (Array.isArray(message)) return message.join(" ") || error.message;
      return message || error.message;
    }
    if (error instanceof Error) {
      return error.message;
    }
    return "Error desconocido";
  }
}
